/**
 * Header — top navigation bar with title and controls.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toggleDarkMode } from '../../store/slices/appSlice';
import { startClassicGame, resetGame } from '../../store/slices/gameSlice';
import { resetKeyboard } from '../../store/slices/keyboardSlice';
import { saveDarkMode } from '../../utils/storage';
import { GAME_STATUS, GAME_MODE } from '../../utils/constants';
import ThemeSelector from './ThemeSelector';

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const darkMode = useSelector((state) => state.app.darkMode);
  const { status, mode } = useSelector((state) => state.game);

  const handleToggleDark = () => {
    dispatch(toggleDarkMode());
    saveDarkMode(!darkMode);
  };

  const handleResetGame = () => {
    if (window.confirm('Mevcut oyunu bırakıp yeni bir oyuna başlamak istediğinize emin misiniz?')) {
      dispatch(resetGame());
      dispatch(resetKeyboard());
      if (mode === GAME_MODE.CLASSIC) {
        dispatch(startClassicGame({ isRandom: true }));
      } else {
        navigate('/custom');
      }
    }
  };

  const isHome = location.pathname === '/';
  const isGamePage = location.pathname === '/classic' || location.pathname.startsWith('/play/');
  const isPlaying = status === GAME_STATUS.PLAYING;

  return (
    <header className="relative flex items-center justify-center h-14 sm:h-16 px-4 border-b border-gray-300 dark:border-gray-700">
      {/* Left: Back button - Absolute positioned */}
      <div className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2">
        {!isHome && (
          <button
            onClick={() => navigate('/')}
            className="p-1.5 sm:p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-xl"
            aria-label="Ana Sayfa"
          >
            ←
          </button>
        )}
      </div>

      {/* Center: Title - Naturally centered by flexbox because parent is justify-center */}
      <h1
        className="text-lg sm:text-2xl md:text-3xl font-bold tracking-wider cursor-pointer select-none whitespace-nowrap px-4 pb-1 max-w-[50%] sm:max-w-none text-center"
        onClick={() => navigate('/')}
      >
        <span className="text-correct">OS</span> Wordle
      </h1>

      {/* Right: Controls - Absolute positioned */}
      <div className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 sm:gap-2">
        {isGamePage && isPlaying && (
          <button
            onClick={handleResetGame}
            className="p-2 rounded-full text-gray-600 dark:text-gray-400 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 transition-colors"
            title="Oyunu Sıfırla"
            aria-label="Oyunu Sıfırla"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
        )}
        <div className="flex items-center">
          <ThemeSelector />
        </div>
        <button
          onClick={handleToggleDark}
          className="p-2 rounded-full text-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          aria-label={darkMode ? 'Aydınlık mod' : 'Karanlık mod'}
        >
          {darkMode ? '☀️' : '🌙'}
        </button>
      </div>
    </header>
  );
};

export default Header;
